// [Project] > [Status] > [Debug: Graph] - how the project's work depends on itself, across
// every agent: its Ready to build checklist (or its next actions) on a timeline or as a
// flow, with what the graph finds beside it. See docs/Y2026-Q4/design-debug-graph.md.
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { useProjectAgentStatus } from "../api";
import { BriefingFrame } from "../components/BriefingFrame";
import { DependencyGraph } from "../components/DependencyGraph";
import {
  DependencyGraphDetail,
  FindingsList,
  FindingsSummary,
} from "../components/DependencyGraphDetail";
import { useReadyCheckEvidence } from "../components/useReadyCheckEvidence";
import { workingSpecialists } from "../utils/standDown";
import {
  buildGraph,
  findings,
  type GraphMode,
  previewAnswer,
  relatedTo,
} from "../utils/dependencyGraph";
import { resolvingActions, workedCheck } from "../utils/nextActions";
import { type GraphView, layoutGraph } from "../utils/dependencyGraphLayout";
import styles from "./ProjectDebugGraphTab.module.css";

const ZOOMS = [
  { label: "Quarter", px: 8 },
  { label: "Month", px: 14 },
  { label: "Week", px: 22 },
] as const;

type LinkToggle = "waits" | "serves" | "send";

export const ProjectDebugGraphTab = () => {
  const { projectId } = useParams();
  const decoded = projectId ? decodeURIComponent(projectId) : "";
  const status = useProjectAgentStatus(decoded || null);
  const data = status.data;

  const [mode, setMode] = useState<GraphMode>("checks");
  const [view, setView] = useState<GraphView>("timeline");
  const [pxPerDay, setPxPerDay] = useState<number>(14);
  const [showFinished, setShowFinished] = useState(false);
  const [crossOnly, setCrossOnly] = useState(false);
  const [kinds, setKinds] = useState<Record<LinkToggle, boolean>>({ waits: true, serves: true, send: true });
  const [selected, setSelected] = useState<string | null>(null);
  const [preview, setPreview] = useState<number | null>(null);
  // The graph gets the width until a card is selected.
  const [detailCollapsed, setDetailCollapsed] = useState(true);

  // The specialists working the project, as the Ready to build overview shows them: one
  // stood down from it holds nothing to draw.
  const working = useMemo(
    () => (data ? { ...data, agents: workingSpecialists(data.agents) } : null),
    [data],
  );
  const graph = useMemo(() => (working ? buildGraph(working, mode) : null), [working, mode]);
  const checks = useMemo(
    () =>
      working
        ? working.agents.flatMap((a) => (a.lists?.ready_checks ?? []).filter((c) => !c.closed))
        : [],
    [working],
  );
  const { cite, modal } = useReadyCheckEvidence(checks);

  // An item that has gone (closed and hidden, or not in this view) leaves nothing selected.
  useEffect(() => {
    if (selected && graph && !graph.nodes.has(selected) && !graph.items.checks.has(selected))
      setSelected(null);
  }, [graph, selected]);

  const drawn = useMemo(() => {
    if (!graph || !data) return null;
    const shown = [...graph.nodes.values()].filter((n) => showFinished || !n.finished);
    const ids = new Set(shown.map((n) => n.id));
    const links = graph.links.filter(
      (l) =>
        kinds[l.kind] &&
        ids.has(l.from) &&
        ids.has(l.to) &&
        (showFinished || !l.satisfied) &&
        (!crossOnly || graph.nodes.get(l.from)?.agent !== graph.nodes.get(l.to)?.agent),
    );
    const agents = working?.agents.map((a) => a.agent) ?? [];
    return { shown, links, agents, layout: layoutGraph(graph, shown, links, view, pxPerDay, agents) };
  }, [graph, data, working, showFinished, kinds, crossOnly, view, pxPerDay]);

  if (status.isLoading) return <p className={styles.muted}>Loading the project&apos;s lists…</p>;
  if (status.error) return <p className={styles.error}>Could not load this project&apos;s lists.</p>;
  if (!data || !graph || !drawn) return null;

  const label = (agent: string) => data.agents.find((a) => a.agent === agent)?.label ?? agent;
  const agentIndex = new Map(drawn.agents.map((a, i) => [a, i]));
  // In the actions view a selected check lights its actions.
  const focus =
    selected === null
      ? null
      : mode === "actions" && graph.items.checks.has(selected)
        ? resolvingActions(graph.items, selected).map((a) => a.action_id)
        : [selected];
  // A selected check with no open action lights nothing rather than dimming everything.
  const related = focus && focus.length > 0 ? relatedTo(graph, focus) : null;
  const previewing =
    preview !== null && selected && mode === "actions" ? previewAnswer(graph, selected, preview) : null;
  const select = (id: string | null) => {
    setPreview(null);
    // An action is not a card in the checklist view: show it where it is drawn.
    if (id && mode === "checks" && graph.items.actions.has(id)) setMode("actions");
    setSelected(id);
    // Selecting a card is asking to see it, so a collapsed detail opens.
    if (id) setDetailCollapsed(false);
  };
  const found = findings(graph);
  const openCount = mode === "checks" ? checks.filter((c) => !c.met).length : 0;
  const open = [...graph.items.actions.values()].filter(({ action }) => !action.closed);
  const openActions = open.length;
  const linked = open.filter(({ action }) => workedCheck(graph.items, action)).length;

  return (
    <BriefingFrame projectId={decoded} level="specialist_details">
    <div className={styles.page}>
      {modal}
      <div className={styles.controls}>
        <span className={styles.group} role="group" aria-label="Draw the graph from">
          <span className={styles.caption}>Draw from</span>
          <button type="button" aria-pressed={mode === "checks"} onClick={() => {
              // The check an action works towards stays selected in the checklist view.
              if (selected && graph.items.actions.has(selected))
                setSelected(graph.items.actions.get(selected)?.action.resolves || null);
              setMode("checks");
              setPreview(null);
            }}>
            Ready to build checklist
          </button>
          <button type="button" aria-pressed={mode === "actions"} onClick={() => setMode("actions")}>
            Next actions
          </button>
        </span>
        <span className={styles.group} role="group" aria-label="Layout">
          <span className={styles.caption}>Layout</span>
          <button type="button" aria-pressed={view === "timeline"} onClick={() => setView("timeline")}>
            Timeline
          </button>
          <button type="button" aria-pressed={view === "flow"} onClick={() => setView("flow")}>
            Flow
          </button>
        </span>
        {view === "timeline" && (
          <span className={styles.group} role="group" aria-label="Zoom">
            {ZOOMS.map((z) => (
              <button key={z.label} type="button" aria-pressed={pxPerDay === z.px} onClick={() => setPxPerDay(z.px)}>
                {z.label}
              </button>
            ))}
          </span>
        )}
        <span className={styles.summary}>
          {mode === "checks"
            ? `${openCount} open checklist items; ${linked} open actions work on them.`
            : `${openActions} open actions.`}
        </span>
        <FindingsSummary
          findings={found}
          onOpen={() => {
            select(null);
            setDetailCollapsed(false);
          }}
        />
      </div>
      <div className={styles.toggles}>
        <label>
          <input type="checkbox" checked={showFinished} onChange={(e) => setShowFinished(e.target.checked)} />{" "}
          {mode === "checks" ? "Show met items" : "Show closed actions"}
        </label>
        <label>
          <input type="checkbox" checked={kinds.waits} onChange={(e) => setKinds({ ...kinds, waits: e.target.checked })} /> Waits for
        </label>
        <label>
          <input type="checkbox" checked={kinds.serves} onChange={(e) => setKinds({ ...kinds, serves: e.target.checked })} /> Works towards a date
        </label>
        {mode === "actions" && (
          <label>
            <input type="checkbox" checked={kinds.send} onChange={(e) => setKinds({ ...kinds, send: e.target.checked })} /> Sent in one email
          </label>
        )}
        <label>
          <input type="checkbox" checked={crossOnly} onChange={(e) => setCrossOnly(e.target.checked)} /> Cross-agent links only
        </label>
      </div>

      <div className={`${styles.main} ${detailCollapsed ? styles.mainCollapsed : ""}`}>
        <div className={styles.canvas}>
          {drawn.shown.length === 0 ? (
            <p className={styles.empty}>
              {mode === "checks"
                ? "No specialist has written a Ready to build checklist on this project yet."
                : "No specialist has written an action on this project yet."}
            </p>
          ) : (
            <DependencyGraph
              graph={graph}
              layout={drawn.layout}
              view={view}
              shown={drawn.shown}
              links={drawn.links}
              agentIndex={agentIndex}
              agentLabel={label}
              selected={selected}
              related={related}
              highlighted={new Set(focus ?? [])}
              preview={previewing}
              onSelect={select}
            />
          )}
        </div>
        {detailCollapsed ? (
          <aside className={styles.detailRail} aria-label="Details">
            <button
              type="button"
              className={styles.collapseButton}
              onClick={() => setDetailCollapsed(false)}
              aria-label="Expand details"
              aria-expanded={false}
              title="Expand details"
            >
              <span aria-hidden>«</span>
            </button>
          </aside>
        ) : (
          <aside className={styles.detail} aria-label="Details">
            <button
              type="button"
              className={`${styles.collapseButton} ${styles.collapseFloat}`}
              onClick={() => setDetailCollapsed(true)}
              aria-label="Collapse details"
              aria-expanded
              title="Collapse details"
            >
              <span aria-hidden>»</span>
            </button>
            {selected ? (
              <DependencyGraphDetail
                id={selected}
                graph={graph}
                data={data}
                projectId={decoded}
                agentLabel={label}
                cite={cite}
                preview={preview}
                onPreview={setPreview}
                onSelect={select}
                onShowActions={() => setMode("actions")}
              />
            ) : (
              <FindingsList findings={found} mode={mode} onSelect={select} />
            )}
          </aside>
        )}
      </div>
    </div>
    </BriefingFrame>
  );
};
